import { getTimeDifference } from '../../presentation/utils/dateUtils';
import { EVENTS_TEXTS } from '../../presentation/utils/texts';

// SensorData type matching backend model
export type SensorData = Record<string, number | boolean | string>;

// Event status enum for type safety
export type EventStatus = 'triggered' | 'resolved' | 'pending' | 'canceled';

export class Event {
  constructor(
    public readonly id: string,
    public readonly alertId: string,
    public readonly alertName: string,
    public readonly sensorId: string,
    public readonly sensorName: string,
    public readonly locationId: string,
    public readonly locationName: string,
    public readonly organizationId: string,
    public readonly createdAt: Date,
    public readonly notifiedAt: Date | null = null,
    public readonly triggeredAt: Date | null = null,
    public readonly confirmed: boolean = false,
    public readonly resolvedAt: Date | null = null,
    public readonly confirmedBy: string[] = [],
    public readonly repeatCountdown: number | null = null,
    public readonly status: EventStatus = 'triggered',
    public readonly data: SensorData = {}
  ) {}

  static create(
    id: string,
    alertId: string,
    alertName: string,
    sensorId: string,
    sensorName: string,
    locationId: string,
    locationName: string,
    organizationId: string,
    data: SensorData,
    status: EventStatus = 'triggered'
  ): Event {
    return new Event(
      id,
      alertId,
      alertName,
      sensorId,
      sensorName,
      locationId,
      locationName,
      organizationId,
      new Date(),
      null,
      null,
      false,
      null,
      [],
      null,
      status,
      data
    );
  }

  isTriggered(): boolean {
    return this.status === 'triggered';
  }

  isPending(): boolean {
    return this.status === 'pending';
  }

  isResolved(): boolean {
    return this.status === 'resolved';
  }

  isCanceled(): boolean {
    return this.status === 'canceled';
  }

  isConfirmed(): boolean {
    return this.status === 'resolved' || this.confirmed;
  }

  hasNotification(): boolean {
    return this.notifiedAt !== null;
  }

  isExpired(): boolean {
    // Consider an event expired if it's more than 24 hours old and not resolved
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return this.createdAt < oneDayAgo && !this.isResolved();
  }

  getTimeSinceCreated(): number {
    return getTimeDifference(this.createdAt);
  }

  getFormattedStatus(): string {
    switch (this.status) {
      case 'triggered':
        return EVENTS_TEXTS.statusTriggered;
      case 'pending':
        return EVENTS_TEXTS.statusPending;
      case 'resolved':
        return EVENTS_TEXTS.statusResolved;
      case 'canceled':
        return EVENTS_TEXTS.statusCanceled;
      default:
        return EVENTS_TEXTS.statusUnknown;
    }
  }

  // Get sensor data value by field name
  getSensorDataValue(field: string): number | boolean | string | undefined {
    return this.data[field];
  }

  // Get all sensor data fields
  getSensorDataFields(): string[] {
    return Object.keys(this.data);
  }

  // Check if event has specific sensor data field
  hasSensorDataField(field: string): boolean {
    return field in this.data;
  }

  // Update status (immutable)
  updateStatus(status: EventStatus): Event {
    const now = new Date();
    
    return new Event(
      this.id,
      this.alertId,
      this.alertName,
      this.sensorId,
      this.sensorName,
      this.locationId,
      this.locationName,
      this.organizationId,
      this.createdAt,
      status === 'pending' ? now : this.notifiedAt,
      status === 'triggered' ? now : this.triggeredAt,
      status === 'resolved',
      status === 'resolved' ? now : this.resolvedAt,
      this.confirmedBy,
      this.repeatCountdown,
      status,
      this.data
    );
  }

  // Confirm event (immutable)
  confirm(confirmedByEmail: string): Event {
    const now = new Date();
    
    return new Event(
      this.id,
      this.alertId,
      this.alertName,
      this.sensorId,
      this.sensorName,
      this.locationId,
      this.locationName,
      this.organizationId,
      this.createdAt,
      this.notifiedAt,
      this.triggeredAt,
      true,
      now,
      [...this.confirmedBy, confirmedByEmail],
      this.repeatCountdown,
      'resolved',
      this.data
    );
  }
}